import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { visitsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { Star, Loader2, X, PenLine } from 'lucide-react';
import { toast } from 'sonner';

const Stars = ({ value, onChange, label }) => (
    <div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-1">{label}</span>
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(s => (
                <button key={s} type="button" onClick={() => onChange(s)} data-testid={`manual-${label}-${s}`}
                    className="transition-transform hover:scale-110">
                    <Star className={cn("w-6 h-6", s <= value ? "text-yellow-400 fill-yellow-400" : "text-white/15")} />
                </button>
            ))}
        </div>
    </div>
);

const Choice = ({ value, onChange, label, options, testid }) => (
    <div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-1">{label}</span>
        <div className="grid grid-cols-3 gap-1.5">
            {options.map(o => (
                <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`${testid}-${o.v}`}
                    className={cn("px-2 py-1.5 rounded-lg border text-xs font-medium transition-all",
                        value === o.v ? "border-[#FF914D]/50 bg-[#FF914D]/10 text-[#FF914D]" : "border-white/10 bg-white/5 text-white/50")}>
                    {o.label}
                </button>
            ))}
        </div>
    </div>
);

const YESNO = [{ v: 'oui', label: 'Oui' }, { v: 'partiellement', label: 'Moyen' }, { v: 'non', label: 'Non' }];
const TIMELINE = [{ v: 'court_terme', label: '< 3 mois' }, { v: '6_mois', label: '3-6 mois' }, { v: 'je_regarde', label: 'Pas pressé' }];
const OFFER = [{ v: 'oui', label: 'Oui' }, { v: 'je_reflechis', label: "Réfléchit" }, { v: 'non', label: 'Non' }];
const FINANCING = [{ v: 'oui', label: 'Validé' }, { v: 'en_cours', label: 'En cours' }, { v: 'non', label: 'Pas encore' }];

export function ManualVisitReportModal({ open, onClose, mandateId, onSaved }) {
    const [saving, setSaving] = useState(false);
    const [f, setF] = useState({
        visitor_first_name: '', visitor_last_name: '', visitor_phone: '',
        rating_overall: 0, rating_luminosity: 0, rating_condition: 0,
        location_ok: '', price_ok: '', purchase_timeline: '', would_make_offer: '', has_financing: '',
        comment: '',
    });
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));

    if (!open) return null;

    const valid = f.visitor_first_name.trim() && f.visitor_last_name.trim() &&
        f.rating_overall > 0 && f.rating_luminosity > 0 && f.rating_condition > 0 &&
        f.location_ok && f.price_ok && f.purchase_timeline && f.would_make_offer && f.has_financing;

    const save = async () => {
        if (!valid) { toast.error('Complétez les champs obligatoires'); return; }
        setSaving(true);
        try {
            const res = await visitsAPI.manualReport(mandateId, f);
            toast.success(`Compte rendu enregistré (lead ${res.data.lead_temperature === 'hot' ? 'CHAUD' : res.data.lead_temperature === 'warm' ? 'tiède' : 'froid'})`);
            onSaved?.();
            onClose();
        } catch {
            toast.error("Échec de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="manual-report-modal">
            <div className="w-full max-w-lg max-h-[92vh] flex flex-col rounded-2xl bg-[#15101d] border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <PenLine className="w-5 h-5 text-[#FF914D]" />
                        <h3 className="font-bold text-white">Saisir un compte rendu</h3>
                    </div>
                    <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="manual-report-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">Prénom *</label>
                            <input value={f.visitor_first_name} onChange={e => set('visitor_first_name', e.target.value)}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF914D]/50" data-testid="manual-first-name" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">Nom *</label>
                            <input value={f.visitor_last_name} onChange={e => set('visitor_last_name', e.target.value)}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF914D]/50" data-testid="manual-last-name" />
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">Téléphone (facultatif)</label>
                        <input value={f.visitor_phone} onChange={e => set('visitor_phone', e.target.value)}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF914D]/50" data-testid="manual-phone" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Stars value={f.rating_overall} onChange={v => set('rating_overall', v)} label="Général" />
                        <Stars value={f.rating_luminosity} onChange={v => set('rating_luminosity', v)} label="Luminosité" />
                        <Stars value={f.rating_condition} onChange={v => set('rating_condition', v)} label="État" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Choice value={f.location_ok} onChange={v => set('location_ok', v)} label="Emplacement OK ?" options={YESNO} testid="manual-location" />
                        <Choice value={f.price_ok} onChange={v => set('price_ok', v)} label="Prix adapté ?" options={YESNO} testid="manual-price" />
                    </div>
                    <Choice value={f.purchase_timeline} onChange={v => set('purchase_timeline', v)} label="Délai d'achat" options={TIMELINE} testid="manual-timeline" />
                    <div className="grid grid-cols-2 gap-3">
                        <Choice value={f.would_make_offer} onChange={v => set('would_make_offer', v)} label="Veut offrir ?" options={OFFER} testid="manual-offer" />
                        <Choice value={f.has_financing} onChange={v => set('has_financing', v)} label="Financement" options={FINANCING} testid="manual-financing" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 block">Commentaire (facultatif)</label>
                        <textarea value={f.comment} onChange={e => set('comment', e.target.value)} rows={2}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm resize-none focus:outline-none focus:border-[#FF914D]/50" data-testid="manual-comment" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 p-5 border-t border-white/10">
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
                    <Button onClick={save} disabled={saving || !valid}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold" data-testid="manual-report-submit">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <PenLine className="w-4 h-4 mr-1.5" />}
                        Enregistrer
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
}
